/**
 * @engine/input — InputDevice Base
 *
 * Abstract base for all input devices.
 * Devices own controls and manage state snapshots per frame.
 */

import type { DeviceId, DeviceType, DeviceState, ControlValue } from '../types';
import type { IInputControl } from '../controls/InputControl';

// Use base IInputControl type for collections (avoids generic variance issues)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyInputControl = IInputControl<any>;

/**
 * Interface for input devices.
 * Each device type (keyboard, mouse, gamepad) implements this.
 */
export interface IInputDevice {
  /** Unique device identifier (e.g., "keyboard/0", "gamepad/0") */
  readonly id: DeviceId;
  /** Device type category */
  readonly type: DeviceType;
  /** Human-readable name (e.g., "Xbox Controller", "Generic Keyboard") */
  readonly name: string;
  /** Is currently connected */
  readonly connected: boolean;

  /**
   * Get all controls on this device
   */
  getAllControls(): ReadonlyMap<string, AnyInputControl>;

  /**
   * Get a specific control by name
   */
  getControl(name: string): AnyInputControl | undefined;

  /**
   * Update device state from backend
   */
  updateControl(name: string, value: ControlValue, time: number): void;

  /**
   * Start of frame — reset deltas
   */
  beginFrame(): void;

  /**
   * End of frame — commit state
   */
  endFrame(): void;

  /**
   * Reset all controls to default
   */
  reset(): void;

  /**
   * Get device state snapshot
   */
  getState(): DeviceState;
}

/**
 * Abstract base class for input devices.
 * Provides common control management logic.
 */
export abstract class InputDevice implements IInputDevice {
  readonly id: DeviceId;
  readonly type: DeviceType;
  readonly name: string;
  readonly vendorId?: number;
  readonly productId?: number;

  private _connected: boolean = true;

  protected readonly controls = new Map<string, AnyInputControl>();

  constructor(type: DeviceType, index: number, name: string, vendorId?: number, productId?: number) {
    this.id = `${type}/${index}`;
    this.type = type;
    this.name = name;
    this.vendorId = vendorId;
    this.productId = productId;
  }

  get connected(): boolean {
    return this._connected;
  }

  setConnected(connected: boolean): void {
    this._connected = connected;
  }

  // ============================================================================
  // Control Access
  // ============================================================================

  getAllControls(): ReadonlyMap<string, AnyInputControl> {
    return this.controls;
  }

  getControl(name: string): AnyInputControl | undefined {
    // Try direct lookup
    let control = this.controls.get(name);
    if (control) return control;

    // Try nested control (e.g., "leftStick/x")
    const parts = name.split('/');
    if (parts.length > 1) {
      control = this.controls.get(parts[0]);
      if (control && 'x' in control) {
        const subName = parts[1];
        if (subName === 'x' || subName === 'y' || subName === 'z') {
          return (control as unknown as Record<string, AnyInputControl>)[subName];
        }
      }
    }

    return undefined;
  }

  // ============================================================================
  // State Updates
  // ============================================================================

  updateControl(name: string, value: ControlValue, time: number): void {
    const control = this.getControl(name);
    if (control) {
      control.update(value, time);
    }
  }

  beginFrame(): void {
    // Nothing by default — subclasses can override
  }

  endFrame(): void {
    for (const control of this.controls.values()) {
      if ('commitFrame' in control) {
        (control as { commitFrame: () => void }).commitFrame();
      }
    }
  }

  reset(): void {
    for (const control of this.controls.values()) {
      control.reset();
    }
  }

  getState(): DeviceState {
    return {
      id: this.id,
      type: this.type,
      name: this.name,
      connected: this._connected,
      vendorId: this.vendorId,
      productId: this.productId,
    };
  }

  // ============================================================================
  // Control Registration (for subclasses)
  // ============================================================================

  protected registerControl(control: AnyInputControl): void {
    this.controls.set(control.name, control);
  }
}
